import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Segmentos } from "./FerramentaUi";
import { IconeExterno } from "./ui";
import { formatarData } from "../lib/dados";
import { aulasEmTexto, duracaoEmTexto, type AulaBonus, type Progresso } from "../lib/aulasBonus";

// As peças da tela de um curso bônus (000177 + 000183). Só apresentação:
// nenhuma decide acesso, nenhuma busca dado, nenhuma grava. Quem grava é
// `paginas/CursoBonus.tsx`, pelas RPCs, e as peças recebem o que aconteceu.
//
// ---------------------------------------------------------------------------
// O que faz esta tela parecer a de um CURSO, e não a de um índice
// ---------------------------------------------------------------------------
// Victor, em 04/09, sobre a primeira versão: *"fazer como se fossem telas de um
// cursinho mesmo, com a opção de marcar concluída e etc."*. As peças abaixo são
// o que uma plataforma de curso tem e a lista da 000177 não tinha:
//
//  * a FAIXA de progresso na cabine — um segmento por aula, aceso NA POSIÇÃO da
//    aula concluída, para o olho ver quais faltam;
//  * o MÓDULO como card, com o próprio "N de M" e a própria barra;
//  * a LINHA da aula com o tique de concluída (que grava), o selo "próxima" na
//    aula em que o aluno parou, e "concluída em" com a data do servidor;
//  * a ANOTAÇÃO por aula, um bilhete com autosave;
//  * o painel de CURSO CONCLUÍDO, que só existe quando é verdade.
//
// ---------------------------------------------------------------------------
// Cor: a família de confirmação é `aviso` (verde), a de atenção é `alerta`
// ---------------------------------------------------------------------------
// Nesta casa `--cf-aviso-*` é VERDE (`#ecfdf5`/`#065f46`) e é a família da
// CONFIRMAÇÃO — o `ChipDeStatus` "concluído" do ForoFácil a usa. O nome engana,
// e já enganou: o aviso mais importante do Leitor de Processos saiu verde por
// causa dele. Aqui "concluída" é `aviso` de propósito (é confirmação), e
// "próxima" é `alerta` (laranja, é atenção). O tique concluído NÃO usa
// `bg-sucesso` com ícone branco: medido, `#5fd0a0` (o `sucesso` do tema escuro)
// dá 1,9 : 1 contra branco — e `aviso-fundo` + `aviso-tinta` trocam com o tema
// e passam nos dois.

/** O tique, em SVG. `width`/`height` intrínsecos pelo motivo da `SetaDireita`. */
export function Tique({ className = "size-4" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/**
 * A faixa de progresso, no rodapé da cabine — SOBRE NAVY, então só cor de
 * marca ou fixa aqui dentro (`text-white`, `text-navy-mute`, o laranja da
 * `Segmentos`); cor semântica neste território é a armadilha central da casa.
 *
 * Um segmento por AULA, aceso na posição da aula concluída (`mapa`): é o que
 * deixa o aluno ver que concluiu 1, 2 e 7, e não "três".
 */
export function FaixaDeProgresso({ aulas, progresso }: { aulas: AulaBonus[]; progresso: Progresso }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-faixa-progresso>
      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-navy-mute">
        Seu progresso
      </span>
      <div className="min-w-48 flex-1">
        <Segmentos
          total={progresso.total}
          acesos={progresso.concluidas}
          mapa={aulas.map((a) => a.concluidaEm !== null)}
          rotulo="Aulas concluídas"
        />
      </div>
      <span data-progresso className="font-display text-sm font-bold tabular-nums text-white">
        {progresso.concluidas} de {progresso.total} · {progresso.porcentagem}%
      </span>
    </div>
  );
}

/**
 * Um módulo do curso: o card com cabeçalho (título, "N de M", a barra) e a lista
 * de aulas dentro. Curso de uma seção só vira UM módulo chamado "Aulas", sem o
 * numeral "Módulo 1" — um único título de grupo repetindo o nome do curso é
 * ruído.
 *
 * `<section role="group" aria-labelledby>` com `<h2>` comum, e nunca
 * `<fieldset><legend>` — a especificação põe o legend SOBRE a borda, e no card
 * arredondado o título sai flutuando fora da caixa (visto na tela em 19/08).
 */
export function Modulo({
  indice,
  titulo,
  mostrarIndice,
  aulas,
  children,
}: {
  indice: number;
  titulo: string;
  mostrarIndice: boolean;
  aulas: AulaBonus[];
  children: ReactNode;
}) {
  const id = `modulo-${indice}`;
  const feitas = aulas.filter((a) => a.concluidaEm !== null).length;
  const completo = aulas.length > 0 && feitas === aulas.length;
  return (
    <section role="group" aria-labelledby={id} data-modulo={indice} className="surface overflow-hidden">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-borda px-4 py-3 sm:px-5">
        <div className="min-w-0 flex-1">
          {mostrarIndice && (
            <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">
              Módulo {indice}
            </p>
          )}
          <h2 id={id} className="font-display text-lg leading-tight font-bold text-titulo">
            {titulo}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {completo && (
            <span className="inline-flex items-center gap-1 rounded-full bg-aviso-fundo px-2 py-0.5 text-[11px] font-semibold text-aviso-tinta">
              <Tique className="size-3" />
              concluído
            </span>
          )}
          <div className="w-20 sm:w-28">
            <Segmentos
              total={aulas.length}
              acesos={feitas}
              mapa={aulas.map((a) => a.concluidaEm !== null)}
              rotulo={`Aulas concluídas em ${titulo}`}
              sobreNavy={false}
            />
          </div>
          <span data-modulo-progresso className="text-xs font-semibold tabular-nums text-cinza">
            {feitas} de {aulas.length}
          </span>
        </div>
      </header>
      <ol className="divide-y divide-borda">{children}</ol>
    </section>
  );
}

/**
 * Uma aula: o numeral, o título com os fatos embaixo, "Assistir" e o tique.
 *
 * Linha de texto, e não card grande — a decisão da 000177 continua: o uso real
 * é PROCURAR uma aula pelo nome, e lista densa encontra melhor que grade.
 *
 * O tique é `<button role="checkbox" aria-checked>`: ele GRAVA (o pai chama a
 * RPC), e o erro de UM tique fica na linha dele — sem leitura não há tela, sem
 * uma escrita há tela e falta um tique (a lição do `alternarFeito` do
 * Calendário). Enquanto grava, o botão desliga: é transitório de um segundo, o
 * único estado em que `disabled:opacity-*` comunica em vez de esconder.
 *
 * "Assistir" continua sendo um LINK para a ponte `/abrir/<curso>/<card>/<aula>`,
 * em aba nova sem `opener` — nada de `onClick` que chama a function e depois
 * abre (as três razões medidas estão em `Abrir.tsx`).
 */
export function LinhaDaAula({
  aula,
  numero,
  para,
  ehProxima,
  ocupado,
  erro,
  anotacaoAberta,
  onConcluir,
  onAlternarAnotacao,
  children,
}: {
  aula: AulaBonus;
  numero: number;
  /** O href da ponte, ou nulo quando a tela não sabe o curso (não deveria acontecer). */
  para: string | null;
  ehProxima: boolean;
  ocupado: boolean;
  erro: string | null;
  anotacaoAberta: boolean;
  onConcluir: (concluida: boolean) => void;
  onAlternarAnotacao: () => void;
  /** O painel da anotação, que o pai monta e mantém montado (ver `Anotacao`). */
  children?: ReactNode;
}) {
  const concluida = aula.concluidaEm !== null;
  const duracao = duracaoEmTexto(aula.duracaoSegundos);
  const idAnotacao = `anotacao-${aula.aulaId}`;

  // Os fatos pequenos da linha, separados por um ponto. Montados numa lista
  // para o separador nunca sobrar no fim nem faltar no meio.
  const fatos: ReactNode[] = [];
  if (duracao) fatos.push(<span key="duracao">{duracao}</span>);
  if (aula.temMaterial) {
    fatos.push(
      <span
        key="material"
        className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 font-semibold text-chip-tinta"
      >
        material de apoio
      </span>,
    );
  }
  if (concluida) {
    fatos.push(
      <span key="concluida" data-concluida-em className="font-semibold text-aviso-tinta">
        concluída em {formatarData(aula.concluidaEm)}
      </span>,
    );
  } else if (aula.abertaEm) {
    fatos.push(<span key="aberta">aberta em {formatarData(aula.abertaEm)}</span>);
  }
  fatos.push(
    <button
      key="anotar"
      type="button"
      onClick={onAlternarAnotacao}
      aria-expanded={anotacaoAberta}
      aria-controls={idAnotacao}
      data-anotar={aula.aulaId}
      className="font-semibold text-link hover:underline"
    >
      {aula.anotacao ? "Ver anotação" : "Anotar"}
    </button>,
  );

  return (
    <li
      data-aula={aula.aulaId}
      data-concluida={concluida ? "sim" : "nao"}
      data-proxima={ehProxima ? "sim" : undefined}
      className={`px-4 py-3.5 sm:px-5 ${ehProxima ? "bg-tile" : ""}`}
    >
      {/* `flex-wrap` com o bloco do título pedindo 14rem: no celular (390px) os
          controles não cabem ao lado e DESCEM para a linha de baixo, alinhados à
          direita. Na primeira captura eles ficavam ao lado e espremiam o título
          em três linhas de dez caracteres. Em telas largas cabe tudo numa linha. */}
      <div className="flex flex-wrap items-start gap-3 sm:gap-4">
        {/* O numeral é `aria-hidden` porque a ordem já é dita ao leitor de tela
            pelo `<ol>` e pelo `sr-only` "Aula N:" do título. Concluída, ele troca
            para a família de confirmação; por fazer, é `text-alerta-tinta` sobre
            `bg-tile` — medido em 04/09 com a sonda que compõe o alfa do fundo,
            5,78 no claro e 6,86 no escuro (`laranja-texto` dava 3,22). */}
        <span
          aria-hidden="true"
          data-numeral
          className={`grid size-9 shrink-0 place-items-center rounded-[11px] font-display text-sm font-bold ${
            concluida ? "bg-aviso-fundo text-aviso-tinta" : "bg-tile text-alerta-tinta"
          }`}
        >
          {numero}
        </span>
        <div className="min-w-0 flex-1 basis-56">
          <p
            data-titulo
            className={`leading-snug font-medium ${concluida ? "text-cinza" : "text-tinta"}`}
          >
            <span className="sr-only">Aula {numero}: </span>
            {aula.titulo}
            {ehProxima && (
              <span
                data-selo-proxima
                className="ml-2 inline-flex items-center rounded-full bg-alerta-fundo px-2 py-0.5 align-middle text-[11px] font-semibold text-alerta-tinta"
              >
                próxima
              </span>
            )}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-cinza">
            {fatos.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-x-2">
                {i > 0 && (
                  <span aria-hidden="true" className="text-borda">
                    ·
                  </span>
                )}
                {f}
              </span>
            ))}
          </p>
          {erro && (
            <p role="alert" className="mt-1 text-xs text-erro-tinta">
              {erro}
            </p>
          )}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:ml-0 sm:gap-3">
          {/* `text-alerta-tinta`, não `text-laranja-texto`: sobre a superfície
              branca o segundo dá 3,83 (medido em 04/09). */}
          {para ? (
            <a
              href={para}
              target="_blank"
              rel="noreferrer"
              data-assistir
              className="inline-flex items-center gap-1 text-sm font-semibold text-alerta-tinta hover:underline"
            >
              Assistir
              <IconeExterno className="size-3.5" />
            </a>
          ) : (
            <span className="text-sm text-cinza">Assistir</span>
          )}
          <button
            type="button"
            role="checkbox"
            aria-checked={concluida}
            aria-label={
              concluida
                ? `Desmarcar a aula ${numero} como concluída`
                : `Marcar a aula ${numero} como concluída`
            }
            title={concluida ? "Concluída — clique para desmarcar" : "Marcar como concluída"}
            disabled={ocupado}
            onClick={() => onConcluir(!concluida)}
            data-concluir={aula.aulaId}
            // O tique por fazer é VISÍVEL, em cinza. A primeira versão o deixava
            // `text-transparent` e só o mostrava no hover — e Victor, na tela:
            // "o tique ficou invisível dentro do círculo, só aparece no hover".
            // Um círculo vazio não diz o que o clique faz; o tique cinza diz.
            // Cor SÓLIDA de propósito: a sonda de contraste da prova ignora o
            // alfa da cor do texto (lê `transparent` como preto), então ela
            // cobra a opacidade em separado.
            className={`grid size-9 shrink-0 place-items-center rounded-full border-2 transition disabled:opacity-60 ${
              concluida
                ? "border-aviso-borda bg-aviso-fundo text-aviso-tinta"
                : "border-borda bg-superficie text-cinza hover:border-laranja hover:text-laranja"
            }`}
          >
            <Tique className="size-4" />
          </button>
        </div>
      </div>
      {children}
    </li>
  );
}

/**
 * O bilhete do aluno sobre UMA aula, com autosave.
 *
 * Texto simples, não editor rico — a mesma decisão da anotação do Gerenciador
 * de Estudos, onde a mediana das 1.267 anotações reais tem 34 caracteres. Salva
 * 900 ms depois da última tecla e mostra o estado ao lado ("salvando…", "salvo",
 * ou a mensagem de erro que o banco deu).
 *
 * O painel fica MONTADO quando fechado (`hidden`), e não desmontado: o timer do
 * autosave morre com o componente, e o aluno que digita e fecha o painel em
 * menos de um segundo perderia a última frase. Com `hidden`, o `setTimeout`
 * sobrevive ao fechamento e a gravação acontece.
 *
 * O texto é DO COMPONENTE depois de montado: o pai recebe o que foi salvo (para
 * trocar "Anotar" por "Ver anotação"), mas não devolve o valor aparado para o
 * campo — senão o espaço que o aluno acabou de digitar sumiria embaixo do
 * cursor a cada gravação.
 */
export function Anotacao({
  aulaId,
  numero,
  inicial,
  aberta,
  onSalvar,
}: {
  aulaId: number;
  numero: number;
  inicial: string | null;
  aberta: boolean;
  /** Grava e devolve a mensagem de erro, ou `null` quando gravou. */
  onSalvar: (texto: string) => Promise<string | null>;
}) {
  const [texto, setTexto] = useState(inicial ?? "");
  const [estado, setEstado] = useState<"parado" | "salvando" | "salvo" | "erro">("parado");
  const [erro, setErro] = useState<string | null>(null);
  const primeiro = useRef(true);
  // A função de salvar muda de identidade a cada render do pai (ela fecha sobre
  // o estado dele). Guardada num ref para o efeito depender SÓ do texto —
  // senão cada tique em outra aula dispararia uma gravação desta anotação.
  const salvar = useRef(onSalvar);
  salvar.current = onSalvar;

  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return;
    }
    setEstado("salvando");
    const t = setTimeout(async () => {
      const e = await salvar.current(texto);
      setErro(e);
      setEstado(e ? "erro" : "salvo");
    }, 900);
    return () => clearTimeout(t);
  }, [texto]);

  const id = `anotacao-${aulaId}`;
  return (
    <div id={id} hidden={!aberta} className="mt-3 pl-12 sm:pl-13">
      <label htmlFor={`${id}-campo`} className="sr-only">
        Anotação da aula {numero}
      </label>
      <textarea
        id={`${id}-campo`}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        rows={3}
        maxLength={10000}
        placeholder="O que você quer lembrar desta aula?"
        className="w-full resize-y rounded-xl border border-borda bg-fundo p-3 text-sm leading-relaxed text-tinta placeholder:text-cinza focus-visible:ring-3 focus-visible:ring-laranja/40 focus-visible:outline-none"
      />
      <p className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-cinza">
        <span>Só você vê esta anotação.</span>
        <span role="status" data-estado-anotacao={estado} className={estado === "erro" ? "text-erro-tinta" : ""}>
          {estado === "salvando"
            ? "salvando…"
            : estado === "salvo"
              ? "salvo"
              : estado === "erro"
                ? erro ?? "não conseguimos salvar agora"
                : texto.length > 9000
                  ? `${texto.length} de 10.000 caracteres`
                  : ""}
        </span>
      </p>
    </div>
  );
}

/**
 * O painel de curso concluído. Só existe quando TODAS as aulas estão marcadas —
 * a tela nunca finge uma conclusão, e "12 aulas" é o número real.
 *
 * `irmaos` são os OUTROS cursos bônus do mesmo curso do aluno: quem acabou o
 * Excel provavelmente quer o Sheets. É a única "sugestão" da tela, e ela é
 * dado (os cards que o aluno tem), não recomendação inventada.
 */
export function CursoConcluido({
  nome,
  progresso,
  duracao,
  irmaos,
  curso,
}: {
  nome: string;
  progresso: Progresso;
  duracao: string | null;
  irmaos: { slug: string; nome: string }[];
  curso: string | null;
}) {
  return (
    <section
      role="group"
      aria-labelledby="curso-concluido"
      data-curso-concluido
      className="surface mt-6 overflow-hidden border-aviso-borda"
    >
      <div className="flex flex-wrap items-center gap-5 p-5 sm:p-6">
        <span
          aria-hidden="true"
          className="grid size-14 shrink-0 place-items-center rounded-full bg-aviso-fundo text-aviso-tinta"
        >
          <Tique className="size-7" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-aviso-tinta">
            Curso concluído
          </p>
          <h2 id="curso-concluido" className="font-display text-xl font-bold text-titulo">
            Você concluiu {nome}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-cinza">
            {aulasEmTexto(progresso.total)}
            {duracao ? ` e ${duracao} de conteúdo` : ""}
            {progresso.ultimaConclusaoEm
              ? `, a última marcada em ${formatarData(progresso.ultimaConclusaoEm)}`
              : ""}
            . As aulas continuam abertas para você rever quando quiser.
          </p>
        </div>
        {irmaos.length > 0 && curso && (
          <div className="flex flex-col gap-2">
            <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">
              Continue com
            </span>
            {irmaos.map((i) => (
              <Link
                key={i.slug}
                to={`/ferramenta/${i.slug}?curso=${encodeURIComponent(curso)}`}
                data-irmao={i.slug}
                className="btn-contorno text-sm"
              >
                {i.nome} →
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
